/**
 * Bosch equipment catalog — alarm panels (B/G/D-Series, AMAX),
 * DIVAR NVR / DIVAR IP recorders, professional detectors (Blue Line Gen2,
 * Commercial Series, Professional Series, OD850), keypads (B915 / B920 /
 * B921C / B942).
 *
 * Every entry below is sourced from a primary Bosch datasheet or the
 * official Bosch Security / KEENFINITY commerce product page. URLs cited
 * inline. Fields are left undefined whenever the source does not list a
 * value — never guessed.
 *
 * Notes on legacy / heritage panels:
 *  - D9412GV4 / D7412GV4 / D7212GV4 — "Heritage" zone-based 9000/7000
 *    series panels documented in the GV4 Installation & Operation Guide.
 *  - B5512 / B6512 / B8512G / B9512G — current B-Series and G-Series
 *    points-based panels. Bosch uses "points" rather than "zones"; we
 *    map points -> zonesMax to keep the picker UI consistent.
 *  - AMAX 2100 / 3000 / 4000 — EU residential / SMB panels.
 *
 * Note on B926: catalog request listed B926 (touchscreen) but Bosch's
 * current SDI2 touchscreen SKU is B942 / B942W. The B926 model is a
 * fire-only keypad. We ship the B942 entry for the touchscreen slot.
 *
 * Note on ISC-PPR1-W16: catalog request listed this as a panic button.
 * Per Bosch datasheet (sourcesecurity / A1 PDF / 123SecurityProducts),
 * it is actually a Professional Series PIR motion detector with 16x21 m
 * coverage. We classify it as outdoor-wall-pir, not panic-button.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// Frequently-cited datasheet hosts — used for sourceUrl assignment.
const BOSCH_CDN =
  'https://resources-boschsecurity-cdn.azureedge.net/public/documents';
const KEENFINITY_COMMERCE = 'https://commerce.keenfinity.tech';
const BOSCH_COMMERCE = 'https://commerce.boschsecurity.com';

export const BOSCH_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // ALARM CONTROL PANELS — B-SERIES (current intrusion + residential fire)
  // Source: Bosch B5512/B6512/B8512G/B9512G datasheets + commerce pages.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-b3512',
    manufacturer: 'Bosch',
    model: 'B3512',
    category: 'alarm-panel',
    tagline: 'Entry B-Series IP panel — 16 points, 2 areas, on-board Ethernet',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/B3512_Control_Panel_Data_sheet_enUS_13300901515.pdf`,
    specs: {
      series: 'B-Series',
      zonesOnboard: 8,
      zonesMax: 16,
      partitions: 2,
      outputsOnboard: 3,
      users: 10,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },
  {
    id: 'bosch-b5512',
    manufacturer: 'Bosch',
    model: 'B5512',
    category: 'alarm-panel',
    tagline: '48-point B-Series IP panel — 4 areas, intrusion + residential fire',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/B5512_Control_Panel_Data_sheet_enUS_10683716747.pdf`,
    specs: {
      series: 'B-Series',
      zonesOnboard: 8,
      zonesMax: 48,
      partitions: 4,
      outputsOnboard: 3,
      outputsMax: 43,
      users: 50,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },
  {
    id: 'bosch-b6512',
    manufacturer: 'Bosch',
    model: 'B6512',
    category: 'alarm-panel',
    tagline: '96-point B-Series IP panel — 6 areas + integrated 4-door access',
    verified: true,
    datasheetUrl:
      'https://www.a1securitycameras.com/content/product_documents/13621/bosch-B6512_Data_sheet_enUS_23172043275-A1.pdf',
    specs: {
      series: 'B-Series',
      zonesOnboard: 8,
      zonesMax: 96,
      partitions: 6,
      outputsOnboard: 3,
      outputsMax: 91,
      users: 100,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      psuAmps: 1.4,
      mobileApp: 'Remote Security Control',
    },
  },
  {
    id: 'bosch-b8512g',
    manufacturer: 'Bosch',
    model: 'B8512G',
    category: 'alarm-panel',
    tagline: '99-point G-Series IP panel — 8 areas, 500 users, commercial intrusion + fire',
    verified: true,
    datasheetUrl: `${BOSCH_COMMERCE}/us/en/B8512G-Control-Panels/`,
    specs: {
      series: 'G-Series',
      zonesOnboard: 8,
      zonesMax: 99,
      partitions: 8,
      outputsOnboard: 3,
      users: 500,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },
  {
    id: 'bosch-b9512g',
    manufacturer: 'Bosch',
    model: 'B9512G',
    category: 'alarm-panel',
    tagline: 'Flagship G-Series IP panel — 599 points, 32 areas, 2000 users, commercial fire',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/B9512G_Data_sheet_enUS_18617768075.pdf`,
    specs: {
      series: 'G-Series',
      zonesOnboard: 8,
      zonesMax: 599,
      partitions: 32,
      outputsOnboard: 3,
      users: 2000,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ALARM CONTROL PANELS — D-SERIES (heritage zone-based, still supported)
  // Source: D9412GV4/D7412GV4/D7212GV4 Installation & Operation Guide.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-d7212gv4',
    manufacturer: 'Bosch',
    model: 'D7212GV4',
    category: 'alarm-panel',
    tagline: 'Heritage D-Series panel — up to 75 points, 4 areas, 100 users',
    verified: true,
    datasheetUrl:
      'https://www.lowvoltageinstaller.com/sites/default/files/2023-01/D9412%20GV4%20-%20D7412%20GV4%20-%20D7212%20GV4%20Installation%20and%20Operation%20Guide%20-%203-12.pdf',
    specs: {
      series: 'D-Series',
      zonesOnboard: 8,
      zonesMax: 75,
      partitions: 4,
      users: 100,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },
  {
    id: 'bosch-d7412gv4',
    manufacturer: 'Bosch',
    model: 'D7412GV4',
    category: 'alarm-panel',
    tagline: 'Heritage D-Series panel — 75 points, 8 areas, 400 users, 2-door access',
    verified: true,
    datasheetUrl:
      'https://www.lowvoltageinstaller.com/sites/default/files/2023-01/D9412%20GV4%20-%20D7412%20GV4%20-%20D7212%20GV4%20Installation%20and%20Operation%20Guide%20-%203-12.pdf',
    specs: {
      series: 'D-Series',
      zonesOnboard: 8,
      zonesMax: 75,
      partitions: 8,
      users: 400,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },
  {
    id: 'bosch-d9412gv4',
    manufacturer: 'Bosch',
    model: 'D9412GV4',
    category: 'alarm-panel',
    tagline: 'Heritage D-Series flagship — 246 points, 32 areas, 1000 users, 8-door access',
    verified: true,
    datasheetUrl:
      'https://www.lowvoltageinstaller.com/sites/default/files/2023-01/D9412%20GV4%20-%20D7412%20GV4%20-%20D7212%20GV4%20Installation%20and%20Operation%20Guide%20-%203-12.pdf',
    specs: {
      series: 'D-Series',
      zonesOnboard: 8,
      zonesMax: 246,
      partitions: 32,
      users: 1000,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      mobileApp: 'Remote Security Control',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ALARM CONTROL PANELS — AMAX (EU residential / SMB)
  // Source: AMAX 2100 / 3000 / 4000 datasheets via Bosch + sourcesecurity.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-amax-2100',
    manufacturer: 'Bosch',
    model: 'AMAX panel 2100',
    category: 'alarm-panel',
    tagline: 'AMAX 2100 — 8 hardwired zones, 2 areas, on-board PSTN voice dialer',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/datasheets/bosch-amax-panel-2100-intruder-alarm-system-control-panel/co-289-ga/amax-panel-2100.pdf',
    specs: {
      series: 'AMAX',
      zonesOnboard: 8,
      zonesMax: 8,
      partitions: 2,
      outputsOnboard: 4,
      users: 64,
      grade: 2,
      communicators: ['PSTN'],
    },
  },
  {
    id: 'bosch-amax-3000',
    manufacturer: 'Bosch',
    model: 'AMAX panel 3000',
    category: 'alarm-panel',
    tagline: 'AMAX 3000 — 8/32 zones, 8 areas, EN 50131 Grade 2',
    verified: true,
    datasheetUrl:
      'https://www.securityinformed.com/datasheets/bosch-amax-panel-3000-intruder-alarm-system-control-panel/co-289-ga/amax-panel-3000.pdf',
    specs: {
      series: 'AMAX',
      zonesOnboard: 8,
      zonesMax: 32,
      partitions: 8,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },
  {
    id: 'bosch-amax-4000',
    manufacturer: 'Bosch',
    model: 'AMAX panel 4000',
    category: 'alarm-panel',
    tagline: 'AMAX 4000 — 16/64 zones, 16 areas, integrated PSTN + IP/GSM expansion',
    verified: true,
    datasheetUrl:
      'https://b2b.dts-system.pl/file/show/file/5e63b3cee22b4/filename/AMAX_4000_family_Data_sheet_enUS_17090780683.pdf',
    specs: {
      series: 'AMAX',
      zonesOnboard: 16,
      zonesMax: 64,
      partitions: 16,
      grade: 2,
      communicators: ['PSTN', 'Ethernet', 'GSM-LTE'],
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // KEYPADS — B-Series (SDI2 bus)
  // Source: Bosch B915 / B920 / B921C / B942 datasheets.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-b915',
    manufacturer: 'Bosch',
    model: 'B915',
    category: 'keypad',
    tagline: '2-line basic LCD keypad — SDI2 bus, B/G-Series compatible',
    verified: true,
    datasheetUrl:
      'https://www.bank-tecsouth.com/uploads/1/2/7/1/127165168/b915_data_sheet.pdf',
    specs: {
      display: 'lcd-text',
      connection: 'SDI2',
      compatibility: ['B3512', 'B4512', 'B5512', 'B6512', 'B8512G', 'B9512G'],
    },
  },
  {
    id: 'bosch-b920',
    manufacturer: 'Bosch',
    model: 'B920',
    category: 'keypad',
    tagline: '2-line alphanumeric LCD keypad — SDI2 bus, 32-char names',
    verified: true,
    datasheetUrl: `${BOSCH_COMMERCE}/us/en/B920-Two-line-Alphanumeric-Keypad/`,
    specs: {
      display: 'lcd-text',
      connection: 'SDI2',
      compatibility: ['B3512', 'B4512', 'B5512', 'B6512', 'B8512G', 'B9512G'],
    },
  },
  {
    id: 'bosch-b921c',
    manufacturer: 'Bosch',
    model: 'B921C',
    category: 'keypad',
    tagline: '2-line capacitive-touch keypad — SDI2, 4 inputs, 3 emergency keys',
    verified: true,
    datasheetUrl:
      'https://www.scansource.com/~/media/security-us/brands/bosch/pdf2/bosch-b921c-specsheet-en',
    specs: {
      display: 'lcd-text',
      connection: 'SDI2',
      compatibility: ['B3512', 'B4512', 'B5512', 'B6512', 'B8512G', 'B9512G'],
      zoneInputs: 4,
    },
  },
  {
    id: 'bosch-b942',
    manufacturer: 'Bosch',
    model: 'B942',
    category: 'keypad',
    tagline: 'Colour-graphic touchscreen keypad — SDI2, 4 inputs, proximity reader',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/B942_Data_sheet_enUS_15489579403.pdf`,
    specs: {
      display: 'touchscreen',
      connection: 'SDI2',
      compatibility: ['B3512', 'B4512', 'B5512', 'B6512', 'B8512G', 'B9512G', 'D7412GV4', 'D9412GV4'],
      readerType: 'em-marin-125khz',
      zoneInputs: 4,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NVRs — DIVAR network 2000 / 3000 / 5000
  // Source: Bosch DIVAR network 2000/3000/5000 datasheets.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-divar-network-2000-16ch',
    manufacturer: 'Bosch',
    model: 'DIVAR network 2000 (DDN-2516)',
    category: 'nvr-recorder',
    tagline: '16-channel desktop NVR — 8 PoE+ ports, 8 MP support, H.265-ready',
    verified: true,
    datasheetUrl:
      'https://www.networkwebcams.co.uk/content/pdf/bosch/bosch-divar-network-2000_data-sheet.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 6,
      poeInPorts: 8,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
    },
  },
  {
    id: 'bosch-divar-network-3000-32ch',
    manufacturer: 'Bosch',
    model: 'DIVAR network 3000 (DDN-3532)',
    category: 'nvr-recorder',
    tagline: '32-channel desktop NVR — 16 PoE+ ports (115 W), 12 MP support',
    verified: true,
    datasheetUrl:
      'https://www.networkwebcams.co.uk/content/pdf/bosch/bosch-divar-network-3000_data-sheet.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 6,
      poeInPorts: 16,
      poeBudgetW: 115,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
    },
  },
  {
    id: 'bosch-divar-network-5000-32ch',
    manufacturer: 'Bosch',
    model: 'DIVAR network 5000 (DRN-5532)',
    category: 'nvr-recorder',
    tagline: '32-channel 1.5U rack NVR — 16 PoE+ ports, 4-bay HDD, 12 MP support',
    verified: true,
    datasheetUrl:
      'https://www.a1securitycameras.com/content/product_documents/17978/bosch-DIVAR_network_5000_Data_sheet_enUS_24101310219-A1.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 6,
      poeInPorts: 16,
      poeBudgetW: 408, // 25.5 W per port × 16 ports
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: '2U',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NVRs — DIVAR IP all-in-one (BVMS-based management appliances)
  // Source: Bosch DIVAR IP AIO 4000/5000/6000/7000 datasheets.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-divar-ip-aio-4000',
    manufacturer: 'Bosch',
    model: 'DIVAR IP all-in-one 4000 (DIP-4420IG)',
    category: 'nvr-recorder',
    tagline: '32-ch BVMS appliance — 2-bay mini-tower, up to 36 TB (2×18 TB)',
    verified: true,
    datasheetUrl: `${BOSCH_COMMERCE}/us/en/DIVAR-IP-all-in-one-4000/p/92189441035/`,
    specs: {
      channels: 32,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 18,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
    },
  },
  {
    id: 'bosch-divar-ip-aio-5000',
    manufacturer: 'Bosch',
    model: 'DIVAR IP all-in-one 5000 (DIP-5240IG)',
    category: 'nvr-recorder',
    tagline: '42-ch BVMS appliance — 4-bay mini-tower, up to 48 TB, 4K recording',
    verified: true,
    datasheetUrl:
      'https://cdn.adiglobaldistribution.us/pim/Original/10022/PB-DIP5240GP_Manufacturer-Brochure_1060203948.pdf',
    specs: {
      channels: 42,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 12,
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
    },
  },
  {
    id: 'bosch-divar-ip-aio-6000',
    manufacturer: 'Bosch',
    model: 'DIVAR IP all-in-one 6000 (DIP-6042IG / 6044 / 6048)',
    category: 'nvr-recorder',
    tagline: '64-ch BVMS appliance — 1U rack, 4-bay enterprise SATA, up to 72 TB',
    verified: true,
    datasheetUrl:
      'https://resources.keenfinity.tech/public/documents/DS_DIVAR_IP_6000_1U_Data_sheet_enUS_12442349835.pdf',
    specs: {
      channels: 64,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 18,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['1', '5'],
      onvif: true,
      formFactor: '1U',
    },
  },
  {
    id: 'bosch-divar-ip-aio-7000',
    manufacturer: 'Bosch',
    model: 'DIVAR IP all-in-one 7000 (DIP-7180-00N)',
    category: 'nvr-recorder',
    tagline: '128-ch BVMS appliance — 2U rack, 8-bay hot-swap, RAID 5, up to 64 TB',
    verified: true,
    datasheetUrl:
      'https://best-vsec.com/documents/datasheet/DS_DIP_7000_2U_R2_Data_sheet_enUS_22822718603.pdf',
    specs: {
      channels: 128,
      maxResolution: '8 MP',
      hddBays: 8,
      hddBayMaxTb: 8,
      codecs: ['H.264', 'H.265'],
      raidLevels: ['1', '5', '6'],
      onvif: true,
      formFactor: '2U',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // PIR MOTION DETECTORS — Blue Line Gen2 (residential / light commercial)
  // Source: Bosch Blue Line Gen2 PIR + TriTech datasheets.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-isc-bpr2-w12',
    manufacturer: 'Bosch',
    model: 'ISC-BPR2-W12',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 94,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.75,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: false,
    tagline: 'Blue Line Gen2 PIR — 12 m × 12 m / 94° FOV, dual Fresnel lenses',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/BlueLine_Gen_2_Data_sheet_enUS_2603228171.pdf`,
  },
  {
    id: 'bosch-isc-bpr2-wp12',
    manufacturer: 'Bosch',
    model: 'ISC-BPR2-WP12',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 94,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.75,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    tagline: 'Blue Line Gen2 pet-immune PIR — 12 m / 94°, animals up to 20 kg',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/BlueLine_Gen_2_Data_sheet_enUS_2603228171.pdf`,
  },
  {
    id: 'bosch-isc-bdl2-w12g',
    manufacturer: 'Bosch',
    model: 'ISC-BDL2-W12G',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 94,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 2.75,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: false,
    tagline: 'Blue Line Gen2 TriTech — 12 m PIR + 10.525 GHz microwave, wall-to-wall',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/BlueLineGen2_Data_sheet_enUS_2614480139.pdf`,
  },
  {
    id: 'bosch-isc-bdl2-wp12g',
    manufacturer: 'Bosch',
    model: 'ISC-BDL2-WP12G',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 94,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 2.75,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    tagline: 'Blue Line Gen2 TriTech pet — 12 m PIR + MW, animals to 20 kg',
    verified: true,
    datasheetUrl: `${BOSCH_CDN}/BlueLineGen2_Data_sheet_enUS_2614480139.pdf`,
  },

  // ─────────────────────────────────────────────────────────────────────
  // PIR MOTION DETECTORS — Commercial Series TriTech
  // Source: Bosch ISC-CDL1-W15G datasheet, SDI link Spec sheet.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-isc-cdl1-w15g',
    manufacturer: 'Bosch',
    model: 'ISC-CDL1-W15G',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.3,
    mountingHeightMaxM: 2.75,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    tagline: 'Commercial Series TriTech — 15 m PIR + 10.525 GHz MW Doppler radar',
    verified: true,
    datasheetUrl:
      'https://www.sdilink.com/Specsheet/ISC-CDL1-W15G.pdf',
  },
  {
    id: 'bosch-isc-cdl1-wa15g',
    manufacturer: 'Bosch',
    model: 'ISC-CDL1-WA15G',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.3,
    mountingHeightMaxM: 2.75,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    tagline: 'Commercial Series TriTech+ — 15 m PIR/MW with anti-mask',
    verified: true,
    datasheetUrl:
      'https://www.sdilink.com/Specsheet/ISC-CDL1-W15G.pdf',
  },

  // ─────────────────────────────────────────────────────────────────────
  // PIR MOTION DETECTORS — Professional Series TriTech (large rooms)
  // Source: Bosch ISC-PDL1-W18x datasheet, alarmax PDF.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-isc-pdl1-w18g',
    manufacturer: 'Bosch',
    model: 'ISC-PDL1-W18G',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 18,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    tagline: 'Professional Series TriTech+ — 18×25 m, tri-focus optics, sensor data fusion',
    verified: true,
    datasheetUrl:
      'https://www.alarmax.com/customer/docs/skudocs/isc-pdl1-w18g-datasheet.pdf',
  },
  {
    id: 'bosch-isc-pdl1-wa18g',
    manufacturer: 'Bosch',
    model: 'ISC-PDL1-WA18G',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 18,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    tagline: 'Professional Series TriTech+ — 18×25 m PIR/MW with anti-mask',
    verified: true,
    datasheetUrl:
      'https://www.alarmax.com/customer/docs/skudocs/isc-pdl1-wa18g-datasheet.pdf',
  },
  {
    id: 'bosch-isc-ppr1-w16',
    manufacturer: 'Bosch',
    model: 'ISC-PPR1-W16',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 16,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    tagline: 'Professional Series PIR — 16×21 m tri-focus optics, EN 50131 G2',
    verified: true,
    datasheetUrl:
      'https://www.a1securitycameras.com/content/product_documents/40798/Bosch-ISC-PPR1-W16-Datasheet-A1.pdf',
  },

  // ─────────────────────────────────────────────────────────────────────
  // PIR MOTION DETECTORS — Outdoor / ceiling / curtain
  // Source: Bosch OD850, DS9370 datasheets.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-od850-f1',
    manufacturer: 'Bosch',
    model: 'OD850-F1',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.1,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    tagline: 'OD850 outdoor TriTech — 15 m PIR + 10.525 GHz MW, IP54',
    verified: true,
    datasheetUrl:
      'https://www.a1securitycameras.com/content/product_documents/9095/bosch-OD850_Datasheet_enUS-A1.pdf',
    ipRating: 'IP54',
  },
  {
    id: 'bosch-ds9370',
    manufacturer: 'Bosch',
    model: 'DS9370',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    rangeM: 21,
    hfovDeg: 360,
    // 21 m diameter at peak mounting height (7.6 m); narrower at low mounting.
    ceilingDiameterByHeightM: {
      2.4: 7,
      3.6: 12,
      4.8: 16,
      7.6: 21,
    },
    mountingHeightDefaultM: 3.6,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 7.6,
    mounting: ['ceiling'],
    detectionMethod: 'pir-mw-dual',
    tagline: 'Panoramic TriTech ceiling detector — 360° × 21 m diameter, PIR + MW',
    verified: true,
    datasheetUrl:
      'https://www.sdilink.com/Specsheet/DS9370.pdf',
  },

  {
    id: 'bosch-ds9371',
    manufacturer: 'Bosch',
    model: 'DS9371',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    rangeM: 21,
    hfovDeg: 360,
    ceilingDiameterByHeightM: {
      2.4: 7,
      3.6: 12,
      4.8: 16,
      7.6: 21,
    },
    mountingHeightDefaultM: 3.6,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 7.6,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    tagline: 'Panoramic PIR-only ceiling detector — 360° × 21 m diameter',
    verified: true,
    datasheetUrl:
      'https://www.sdilink.com/Specsheet/DS9370.pdf',
  },

  // ─────────────────────────────────────────────────────────────────────
  // GLASS-BREAK / SEISMIC
  // Source: Bosch ISC-SM-90 datasheet (assets.catalog.boschbuildingtechnologies).
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'bosch-isc-sm-90',
    manufacturer: 'Bosch',
    model: 'ISC-SM-90',
    category: 'glass-break',
    tagline: 'Seismic detector — 90 m² area, 5 m radius on concrete, safes/vaults/LWS',
    verified: true,
    datasheetUrl:
      'https://assets.catalog.boschbuildingtechnologies.com/public/documents/ISC_SM_90__Data_sheet_enUS_9007200674545547.pdf',
    specs: {
      method: 'piezo-shock',
      rangeM: 5,
    },
  },
];
